import { ThumbsDown, ThumbsUp } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../../lib/utils';

export type FeedbackPanelThumb = 'up' | 'down' | null;

export interface FeedbackPanelLabels {
  prompt: string;
  placeholder: string;
  submittedText: string;
  thumbsUpAriaLabel?: string;
  thumbsDownAriaLabel?: string;
}

export interface FeedbackPanelProps {
  /** Selected thumb (controlled). `null` = no selection. */
  thumb: FeedbackPanelThumb;
  /** Current textarea value (controlled). */
  comment: string;
  /** When `true`, the textarea is replaced with the `submittedText` label. */
  submitted: boolean;
  /** Fires on either thumb click (parent decides toggle vs. exclusive semantics). */
  onThumbClick: (thumb: 'up' | 'down') => void;
  onCommentChange: (value: string) => void;
  /** Fires on textarea blur. Parent decides whether to advance `submitted`. */
  onCommentBlur: () => void;
  labels: FeedbackPanelLabels;
  className?: string;
}

/**
 * `FeedbackPanel` — thumbs-up/down + free-text comment affordance for any
 * AI-output surface ("What do you think about this analysis?"). Pure
 * presentational; parent owns the state machine (thumb selection, comment
 * value, submitted flag). After submission, the textarea swaps for the
 * `submittedText` confirmation label.
 */
export function FeedbackPanel({
  thumb,
  comment,
  submitted,
  onThumbClick,
  onCommentChange,
  onCommentBlur,
  labels,
  className,
}: FeedbackPanelProps): React.ReactElement {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onThumbClick('up')}
          aria-label={labels.thumbsUpAriaLabel ?? 'Thumbs up'}
          aria-pressed={thumb === 'up'}
          className={cn(
            'inline-flex h-4 w-4 items-center justify-center rounded-[2px] focus:outline-none focus:ring-2 focus:ring-dawn-orange',
            thumb === 'up' ? 'text-dawn-orange' : 'text-dawn-black'
          )}
        >
          <ThumbsUp
            className="h-4 w-4"
            strokeWidth={1.5}
            fill={thumb === 'up' ? 'currentColor' : 'none'}
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          onClick={() => onThumbClick('down')}
          aria-label={labels.thumbsDownAriaLabel ?? 'Thumbs down'}
          aria-pressed={thumb === 'down'}
          className={cn(
            'inline-flex h-4 w-4 items-center justify-center rounded-[2px] focus:outline-none focus:ring-2 focus:ring-dawn-orange',
            thumb === 'down' ? 'text-dawn-orange' : 'text-dawn-black'
          )}
        >
          <ThumbsDown
            className="h-4 w-4"
            strokeWidth={1.5}
            fill={thumb === 'down' ? 'currentColor' : 'none'}
            aria-hidden="true"
          />
        </button>
      </div>
      {submitted ? (
        <p className="font-outfit text-[13px] leading-[1.3] text-grey-500">
          {labels.submittedText}
        </p>
      ) : (
        <textarea
          rows={1}
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          onBlur={onCommentBlur}
          placeholder={labels.placeholder}
          aria-label={labels.prompt}
          className="block h-10 w-full resize-none rounded-[4px] border border-grey-400 px-2 py-[10px] font-outfit text-[13px] leading-[1.3] text-dawn-black placeholder:text-grey-400 focus:outline-none focus:ring-2 focus:ring-dawn-orange"
        />
      )}
    </div>
  );
}

FeedbackPanel.displayName = 'FeedbackPanel';
